import { useTranslation } from 'react-i18next';
import type { HomeQuestion } from '@/types/survey.types';
import type { QuizAnswer } from '../hooks/useInlineQuiz';
import { InitialsAvatar } from '@/components/common';

const LIKERT_FACES = [
  { value: 4, color: '#342F82', path: 'M11 21c2 4 12 4 14 0', eyeY: 14, labelKey: 'survey.likert.always'    },
  { value: 3, color: '#10B981', path: 'M12 22c2 2 10 2 12 0', eyeY: 15, labelKey: 'survey.likert.usually'   },
  { value: 2, color: '#F59E0B', path: 'M12 25c2-2 10-2 12 0', eyeY: 15, labelKey: 'survey.likert.sometimes' },
  { value: 1, color: '#EF4444', path: 'M12 26c2-3 10-3 12 0', eyeY: 15, labelKey: 'survey.likert.rarely'    },
] as const;

export { LIKERT_FACES };

interface MatrixBodyProps {
  q: HomeQuestion;
  eIdx: number;
  matrixAnswers: QuizAnswer[];
  participants: string[];
  setMatrixAnswer: (eIdx: number, pIdx: number, value: QuizAnswer) => void;
}

export function MatrixBody({ q, eIdx, matrixAnswers, participants, setMatrixAnswer }: MatrixBodyProps) {
  const { t } = useTranslation();
  const BEHAVIOR_COLORS = ['#342F82', '#10B981', '#F59E0B', '#EF4444'];

  // ── Likert matrix — emoji faces as columns ─────────────────────────────────
  if (q.type === 'likert') {
    const NO_OBS_COLOR = '#9ca3af';
    return (
      <div className="home-mx-table">
        <div className="home-mx-head">
          <div className="home-mx-person-cell" />
          {LIKERT_FACES.map(face => (
            <div key={face.value} className="home-mx-col" data-face-value={face.value}>
              <svg width="22" height="22" viewBox="0 0 36 36" fill="none">
                <circle cx="18" cy="18" r="16" fill={face.color} opacity=".15" stroke={face.color} strokeWidth="1.5" />
                <circle cx="12" cy={face.eyeY} r="2" fill={face.color} />
                <circle cx="24" cy={face.eyeY} r="2" fill={face.color} />
                <path d={face.path} stroke={face.color} strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              <span className="home-mx-col-label" style={{ color: face.color }}>{t(face.labelKey)}</span>
            </div>
          ))}
          <div className="home-mx-col">
            <span className="home-mx-col-label" style={{ color: NO_OBS_COLOR }}>{t('survey.likert.notObservable')}</span>
          </div>
        </div>
        {participants.map((name, pIdx) => {
          const sel = matrixAnswers[pIdx] ?? null;
          return (
            <div key={pIdx} className="home-mx-row">
              <div className="home-mx-person-cell">
                <InitialsAvatar name={name} baseClass="home-mx-av" colorIndex={pIdx} />
                <span className="home-mx-name">{name}</span>
              </div>
              {LIKERT_FACES.map(face => (
                <div key={face.value} className="home-mx-col">
                  <button
                    type="button"
                    className={`home-mx-radio${sel === face.value ? ' selected' : ''}`}
                    style={sel === face.value ? { borderColor: face.color, background: `${face.color}81` } : { borderColor: `${face.color}40` }}
                    onClick={() => setMatrixAnswer(eIdx, pIdx, face.value)}
                    aria-label={`${name} — ${t(face.labelKey)}`}
                  />
                </div>
              ))}
              <div className="home-mx-col">
                <button
                  type="button"
                  className={`home-mx-radio${sel === 0 ? ' selected' : ''}`}
                  style={sel === 0 ? { borderColor: NO_OBS_COLOR, background: `${NO_OBS_COLOR}50` } : { borderColor: `${NO_OBS_COLOR}60` }}
                  onClick={() => setMatrixAnswer(eIdx, pIdx, 0)}
                  aria-label={`${name} — ${t('survey.likert.notObservable')}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── Multiple / Behaviors matrix — text options as columns ──────────────────
  if (q.type === 'multiple' || q.type === 'behaviors') {
    const opts = q.options ?? [];
    const isBehaviors = q.type === 'behaviors';
    return (
      <div className="home-mx-mc" style={{ overflowX: 'auto' }}>
        <div className="home-mx-mc-head">
          <div className="home-mx-person-cell" />
          {opts.map((opt, oi) => (
            <div key={oi} className="home-mx-mc-col">
              <span className="home-mx-mc-label" style={isBehaviors ? { color: BEHAVIOR_COLORS[oi] } : {}}>{opt}</span>
            </div>
          ))}
        </div>
        {participants.map((name, pIdx) => {
          const sel = matrixAnswers[pIdx] ?? null;
          return (
            <div key={pIdx} className="home-mx-mc-row">
              <div className="home-mx-person-cell">
                <InitialsAvatar name={name} baseClass="home-mx-av" colorIndex={pIdx} />
                <span className="home-mx-name">{name}</span>
              </div>
              {opts.map((_, oi) => {
                const c = isBehaviors ? BEHAVIOR_COLORS[oi] : '#342F82';
                return (
                  <div key={oi} className="home-mx-mc-col">
                    <button
                      type="button"
                      className={`home-mx-dot-btn${sel === oi ? ' selected' : ''}`}
                      style={sel === oi ? { background: c, borderColor: c } : {}}
                      onClick={() => setMatrixAnswer(eIdx, pIdx, oi)}
                      aria-label={`${name} — ${opts[oi]}`}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }

  // ── Open text matrix — one textarea per participant ────────────────────────
  return (
    <div className="home-mx-open">
      {participants.map((name, pIdx) => (
        <div key={pIdx} className="home-mx-open-row">
          <div className="home-mx-person-cell home-mx-person-cell--open">
            <InitialsAvatar name={name} baseClass="home-mx-av" colorIndex={pIdx} />
            <span className="home-mx-name">{name}</span>
          </div>
          <textarea
            className="home-q-open home-mx-textarea"
            rows={2}
            placeholder={t('survey.openPlaceholder', { defaultValue: 'Escribe aquí…' })}
            value={typeof matrixAnswers[pIdx] === 'string' ? (matrixAnswers[pIdx] as string) : ''}
            onChange={e => setMatrixAnswer(eIdx, pIdx, e.target.value)}
          />
        </div>
      ))}
    </div>
  );
}
